import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./FoodMenu.css";
import "../MenuWorkflow.css";
import "./MenuHistoryModal/MenuHistoryModal.css";
import {
  activateMenuAPI,
  archiveItemAPI,
  archiveMenuAPI,
  archiveSectionAPI,
  copyMenuContentAPI,
  createItemAPI,
  createSectionAPI,
  duplicateMenuAPI,
  fetchMenuAdditionsAPI,
  fetchMenuHistoryAPI,
  fetchMenuHistoryPageAPI,
  importIntoMenuAPI,
  removeItemImageAPI,
  reorderItemsAPI,
  reorderSectionsAPI,
  restoreMenuAPI,
  setItemBranchVisibilityAPI,
  setItemHiddenAPI,
  updateItemAPI,
  updateMenuPropsAPI,
  updateSectionAPI,
  uploadItemImageAPI,
} from "../../../utils/api/menu";
import { handleExportMenuExcel, readMenuFile, transformMenuData } from "../../../utils/exportExcel";
import { Context } from "../../../context/Context";
import { Archive, ArrowDown, ArrowRightLeft, ArrowUp, Building2, Button, CircleX, Copy, Eye, EyeOff, History, ImageOff, ImagePlus, Input, NoticeBox, Pencil, Plus, Select } from "../../ui";
import { ConfirmModal } from "../../modals/Confirm";
import FoodItemForm from "./FoodItemForm/FoodItemForm";
import { emptyItem, formToPayload, itemToForm } from "./FoodItemForm/itemFormModel";
import FoodTopbar from "./FoodTopbar/FoodTopbar";
import FoodSidebar from "./FoodSidebar/FoodSidebar";
import FoodSection from "./FoodSection/FoodSection";
import FoodImageModal from "./FoodImageModal/FoodImageModal";
import AddSectionModal from "./AddSectionModal/AddSectionModal";
import MenuAdditionsModal from "./MenuAdditionsModal/MenuAdditionsModal";
import MenuHistoryModal from "./MenuHistoryModal/MenuHistoryModal";
import useMenuHistory, { MENU_HISTORY_PAGE_SIZE } from "./MenuHistoryModal/useMenuHistory";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import MenuSelectionModal from "../copyModal/CopyModal";
import MenuBranchesPanel from "../branches/MenuBranchesPanel";
import ArchivedContentPanel from "../dialogs/ArchivedContentPanel";
import ItemVisibilityDialog from "../dialogs/ItemVisibilityDialog";
import MoveItemDialog from "../dialogs/MoveItemDialog";
import { menuActionsText } from "../menuActionsText";
import { localized, menuErrorMessage, useMenuRole } from "../menuRole";
import { useAccess } from "../../../access/hooks/useAccess";
import { SelectedMenuSummary } from "../summary/MenuSummary";
import { summaryText } from "../summary/summaryText";
import { ITEM_FILTERS, matchesItemFilter } from "../../../utils/menuHealth";
import { fetchAppSendsAPI, fetchSendEligibilityAPI } from "../../../utils/api/menuAppSend";
import SendHistoryDialog from "../sendToApp/SendHistoryDialog";
import { sendToAppText } from "../sendToApp/sendToAppText";
import SendToAppBar from "../sendToApp/SendToAppBar";

const move = (list, from, to) => {
  const next = [...list];
  const [moving] = next.splice(from, 1);
  next.splice(to, 0, moving);
  return next;
};

// Every change is saved immediately (and recorded in the menu history).
// `filter` is owned by the Menu page when given (so its overview cards can
// set it); otherwise it is local.
// `sendMode` (owned by the Menu page, switched on from the Item actions) is "Send Items to App" selection mode.
export default function FoodMenu({ menu, menus = [], allMenus = [], restaurants = [], onSelectMenu, onMenuChange, filter: filterProp, onFilterChange, sendMode = false, onSendModeChange, canSend = false }) {
  const { lang } = useContext(Context);
  const t = (ar, en) => (lang === "ar" ? ar : en);
  const role = useMenuRole();
  const { can } = useAccess();
  const editable = role.canWrite && menu.lifecycle !== "archived";
  // the selected-menu summary is hidden from branch users (menu.summary.view)
  const canManageAdmin = can("menu.summary.view");

  const [panel, setPanel] = useState(null);
  const [search, setSearch] = useState("");
  const [localFilter, setLocalFilter] = useState("all");
  const filter = filterProp ?? localFilter;
  const setFilter = onFilterChange ?? setLocalFilter;
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [expandedCard, setExpandedCard] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [menuAdditions, setMenuAdditions] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const [itemForm, setItemForm] = useState(null);
  const [sectionForm, setSectionForm] = useState(null);
  const [imageTarget, setImageTarget] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [history, setHistory] = useState({ open: false, loading: false, list: [] });
  // MENU_HISTORY_PAGINATION: menu / item history in pages with "load older" (off: the single request in openHistory).
  const pagedHistory = isFeatureEnabled(FEATURE_NAMES.MENU_HISTORY_PAGINATION);
  const historyItemId = useRef();
  const pagedMenuHistory = useMenuHistory((cursor) =>
    fetchMenuHistoryPageAPI(menu._id, { itemId: historyItemId.current, limit: MENU_HISTORY_PAGE_SIZE, ...cursor }),
  );
  const closePagedMenuHistory = pagedMenuHistory.close;
  // Another menu never inherits the open history or an answer still on its way.
  useEffect(() => {
    closePagedMenuHistory();
  }, [menu._id, closePagedMenuHistory]);
  const [copyContext, setCopyContext] = useState(null);
  const [nameForm, setNameForm] = useState(null);
  const [sectionOrder, setSectionOrder] = useState(null);
  const [itemOrder, setItemOrder] = useState(null);
  const [drag, setDrag] = useState(null);
  const [dialog, setDialog] = useState(null);
  // Send Items to App: ids ticked in selection mode, and per-item send history.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [itemSends, setItemSends] = useState({});
  const [sendHistoryItem, setSendHistoryItem] = useState(null);
  // Backend send eligibility per item (null until loaded): { [itemId]: { eligible, hidden, missing } }.
  const [eligibility, setEligibility] = useState(null);
  // Required choice in Send Mode: "add" | "update" | "delete" ("" until picked).
  const [sendAction, setSendAction] = useState("");
  // The action the loaded eligibility was computed for (it follows sendAction a moment later).
  const [eligibilityAction, setEligibilityAction] = useState("");
  const latestAction = useRef("");
  const a = menuActionsText(lang);
  const sendActive = sendMode && canSend && menu.lifecycle !== "archived";

  useEffect(() => {
    setSelectedOptions({});
    setSectionOrder(null);
    setItemOrder(null);
    setSelectedBranchId("");
    // Selection never follows the user to another menu.
    setSelectedIds(new Set());
    onSendModeChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu._id]);

  useEffect(() => {
    if (!sendActive) {
      setSelectedIds(new Set());
      setSendAction("");
    }
    else setPanel((current) => (current === "additions" ? null : current));
  }, [sendActive]);

  // Send status of this menu's items. The backend derives it from the item's
  // current app-facing content, so it is re-read whenever the menu saved a change
  // (`menu.version`) or the restaurant's additions changed.
  useEffect(() => {
    let alive = true;
    fetchAppSendsAPI(menu._id)
      .then((sends) => alive && setItemSends(sends || {}))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [menu._id, menu.version, menuAdditions]);
  useEffect(() => {
    setItemSends({});
    setSendHistoryItem(null);
  }, [menu._id]);

  // Which items may be sent. Evaluated by the backend (the same rules the send API
  // enforces) whenever Send Mode is on and the menu or its additions changed.
  const loadEligibility = useCallback(async () => {
    // Until an action is picked the stricter add/update rules apply; delete relaxes them.
    const effective = sendAction || "update";
    latestAction.current = effective;
    try {
      const map = await fetchSendEligibilityAPI(menu._id, effective);
      if (latestAction.current !== effective) return; // a newer request superseded this one
      setEligibility(map);
      setEligibilityAction(effective);
    } catch {
      setEligibility({});
      setEligibilityAction(effective);
      setNotice({ type: "error", text: sendToAppText(lang).eligibilityFailed });
    }
  }, [menu._id, lang, sendAction]);
  useEffect(() => {
    if (!sendActive) return setEligibility(null);
    // The previous result stays on screen while the new one loads (no flicker).
    loadEligibility();
    return undefined;
  }, [sendActive, loadEligibility, menu.version, menuAdditions]);

  const loadAdditions = useCallback(async () => {
    try {
      setMenuAdditions(await fetchMenuAdditionsAPI(menu.restaurant._id));
    } catch {
      setMenuAdditions([]);
    }
  }, [menu.restaurant._id]);
  useEffect(() => {
    loadAdditions();
  }, [loadAdditions]);

  const orderedSections = useMemo(() => {
    if (!sectionOrder) return menu.sections;
    const byId = new Map(menu.sections.map((s) => [String(s._id), s]));
    return sectionOrder.map((id) => byId.get(id)).filter(Boolean).map((section, index) => ({ ...section, order: index + 1 }));
  }, [menu.sections, sectionOrder]);

  const visibleSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const assignedBranches = new Set((menu.branches || []).map(String));
    const sections = orderedSections.map((section) => {
      let items = section.items;
      if (itemOrder && itemOrder.sectionId === String(section._id)) {
        const byId = new Map(items.map((i) => [String(i._id), i]));
        items = itemOrder.ids.map((id) => byId.get(id)).filter(Boolean).map((item, index) => ({ ...item, order: index + 1 }));
      }
      items = items.filter((item) => {
        const text = [item.name?.ar, item.name?.en, item.description?.ar, item.description?.en].join(" ").toLowerCase();
        if (q && !text.includes(q)) return false;
        return matchesItemFilter(filter, item, { assignedBranches, selectedBranchId });
      });
      return { ...section, items };
    });
    // A filter shows only the sections that have matching items.
    return filter === "all" ? sections : sections.filter((section) => section.items.length > 0);
  }, [orderedSections, itemOrder, search, filter, selectedBranchId, menu.branches]);

  // Only items that still exist (live) in this menu can stay selected.
  const selectedItemIds = useMemo(() => {
    const live = new Set(menu.sections.flatMap((section) => section.items.map((entry) => String(entry._id))));
    return new Set([...selectedIds].filter((id) => live.has(id) && eligibility?.[id]?.eligible));
  }, [selectedIds, menu.sections, eligibility]);
  // null = can be sent, { pending } = still checking, otherwise the backend's reasons.
  const sendBlockFor = (entry) => {
    if (!eligibility) return { pending: true };
    const result = eligibility[String(entry._id)];
    if (!result) return { pending: true };
    return result.eligible ? null : result;
  };
  const toggleSelected = (entry) =>
    !sendBlockFor(entry) &&
    setSelectedIds((current) => {
      const next = new Set(current);
      const id = String(entry._id);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Select all = every item shown that can be sent; hidden / incomplete ones are skipped.
  const shownItems = visibleSections.flatMap((section) => section.items);
  const availableShown = shownItems.filter((entry) => !sendBlockFor(entry));
  const selectAllShown = () => setSelectedIds(new Set([...selectedItemIds, ...availableShown.map((entry) => String(entry._id))]));

  const branchOptions = useMemo(() => {
    const assigned = new Set((menu.branches || []).map(String));
    return (menu.restaurant.branches || []).filter((b) => assigned.has(String(b._id)));
  }, [menu.branches, menu.restaurant.branches]);

  useEffect(() => {
    if (role.isBranchUser && !selectedBranchId && branchOptions.length) setSelectedBranchId(String(branchOptions[0]._id));
  }, [role.isBranchUser, selectedBranchId, branchOptions]);

  // Filtering from a summary card must land on the items list.
  useEffect(() => {
    if (filter !== "all") setPanel((current) => (current === "additions" ? null : current));
  }, [filter]);

  const focusItems = (next) => {
    setFilter(next);
    requestAnimationFrame(() => document.getElementById("menu-items-toolbar")?.scrollIntoView?.({ behavior: "smooth", block: "start" }));
  };
  const summaryLabels = summaryText(lang);

  // ------------------------------------------------------------ save helper
  // Every change goes through here. Handler-level authorization: re-checked right
  // before the request, against this menu's restaurant (default: menu.manage).
  const save = async (action, success = t("تم الحفظ", "Saved"), guard) => {
    const permission = guard?.permission || "menu.manage";
    const resource = guard?.resource || { restaurantId: menu.restaurant?.id };
    if (!can(permission, resource)) {
      const denied = Object.assign(new Error("Not allowed"), { status: 403 });
      setNotice({ type: "error", text: menuErrorMessage(denied, lang) });
      throw denied;
    }
    setBusy(true);
    setNotice(null);
    try {
      const result = await action();
      if (result?.menu) onMenuChange(result.menu);
      setNotice({ type: "success", text: success });
      return result;
    } catch (error) {
      setNotice({ type: "error", text: menuErrorMessage(error, lang) });
      throw error;
    } finally {
      setBusy(false);
    }
  };
  const safe = (fn) => async (...args) => {
    try {
      return await fn(...args);
    } catch {
      return undefined;
    }
  };

  // ------------------------------------------------------------ item actions
  const saveItem = safe(async () => {
    const payload = formToPayload(itemForm.data);
    if (itemForm.mode === "add") await save(() => createItemAPI(menu._id, itemForm.section._id, payload));
    else await save(() => updateItemAPI(menu._id, itemForm.item._id, payload));
    setItemForm(null);
  });

  const openImage = (item) => {
    setImageTarget(item);
    setImageFile(null);
    setImagePreview(item.image || "");
  };
  const toggleHidden = safe((item, hidden) => save(() => setItemHiddenAPI(menu._id, item._id, hidden)));
  // A dialog saved on its own: show the fresh menu (and summaries) + feedback.
  const applySaved = (next, message) => {
    onMenuChange(next);
    if (message) setNotice({ type: "success", text: message });
  };

  // Quick hide/show for the branch picked in the toolbar (branch users' main action).
  const itemHandlers = {
    onToggleBranch: safe((item, branchId, currentlyHidden) => save(() => setItemBranchVisibilityAPI(menu._id, item._id, branchId, currentlyHidden), undefined, { permission: "menu.branch.visibility", resource: { restaurantId: menu.restaurant?.id, branchId: (menu.restaurant?.branches || []).find((b) => String(b._id) === String(branchId))?.name } })),
  };
  if (!branchOptions.length || !role.canBranchVisibility) delete itemHandlers.onToggleBranch;

  // Reordering by one step (the keyboard/touch alternative to drag and drop).
  const liveIds = (list) => list.map((entry) => String(entry._id));
  const stepSection = safe((index, to) => save(() => reorderSectionsAPI(menu._id, move(liveIds(menu.sections), index, to))));
  const stepItem = safe((section, index, to) => save(() => reorderItemsAPI(menu._id, section._id, move(liveIds(section.items), index, to))));

  // Only actions that can succeed for this menu state and role are listed.
  const sectionActions = (section) => {
    const index = menu.sections.findIndex((s) => String(s._id) === String(section._id));
    return [
      { key: "add", label: a.addItem, icon: <Plus size={16} />, hidden: !editable, onClick: () => setItemForm({ mode: "add", section, data: emptyItem() }) },
      { key: "rename", label: a.renameSection, icon: <Pencil size={16} />, hidden: !editable, onClick: () => setSectionForm({ mode: "rename", section, data: { name: { ar: section.name?.ar || "", en: section.name?.en || "" } } }) },
      { key: "up", label: a.moveUp, icon: <ArrowUp size={16} />, hidden: !editable || index <= 0, onClick: () => stepSection(index, index - 1) },
      { key: "down", label: a.moveDown, icon: <ArrowDown size={16} />, hidden: !editable || index < 0 || index >= menu.sections.length - 1, onClick: () => stepSection(index, index + 1) },
      { key: "copy", label: a.copySection, icon: <Copy size={16} />, hidden: !editable, onClick: () => setCopyContext({ type: "SECTION", source: { menuId: menu._id, sectionId: section._id } }) },
      { key: "archive", label: a.archiveSection, icon: <Archive size={16} />, tone: "danger", divider: true, hidden: !editable, onClick: () => setConfirm({ kind: "section", target: section }) },
    ].map((entry) => ({ ...entry, disabled: busy }));
  };

  const itemActions = (section, item) => {
    const live = menu.sections.find((s) => String(s._id) === String(section._id));
    const index = live ? live.items.findIndex((i) => String(i._id) === String(item._id)) : -1;
    const hidden = Boolean(item.visibility?.hidden);
    const canBranchVisibility = menu.lifecycle === "active" && branchOptions.length > 0 && role.canBranchVisibility;
    return [
      { key: "edit", label: a.edit, icon: <Pencil size={16} />, hidden: !editable, onClick: () => setItemForm({ mode: "edit", item, data: itemToForm(item) }) },
      { key: "image", label: item.image ? a.changeImage : a.addImage, icon: <ImagePlus size={16} />, hidden: !editable, onClick: () => openImage(item) },
      { key: "removeImage", label: a.removeImage, icon: <ImageOff size={16} />, hidden: !editable || !item.image, onClick: () => setConfirm({ kind: "removeImage", target: item }) },
      { key: "hidden", label: hidden ? a.showAll : a.hideAll, icon: hidden ? <Eye size={16} /> : <EyeOff size={16} />, divider: true, hidden: !editable, onClick: () => toggleHidden(item, !hidden) },
      { key: "branches", label: a.branchVisibility, icon: <Building2 size={16} />, hidden: !canBranchVisibility, onClick: () => setDialog({ kind: "visibility", item }) },
      { key: "move", label: a.moveToSection, icon: <ArrowRightLeft size={16} />, divider: true, hidden: !editable || menu.sections.length < 2, onClick: () => setDialog({ kind: "move", item, sectionId: section._id }) },
      { key: "up", label: a.moveUp, icon: <ArrowUp size={16} />, hidden: !editable || index <= 0, onClick: () => stepItem(live, index, index - 1) },
      { key: "down", label: a.moveDown, icon: <ArrowDown size={16} />, hidden: !editable || index < 0 || index >= live.items.length - 1, onClick: () => stepItem(live, index, index + 1) },
      { key: "copy", label: a.copyItem, icon: <Copy size={16} />, hidden: !editable, onClick: () => setCopyContext({ type: "ITEM", source: { menuId: menu._id, itemId: item._id } }) },
      { key: "history", label: a.itemHistory, icon: <History size={16} />, divider: true, onClick: () => openHistory(item._id) },
      { key: "archive", label: a.archiveItem, icon: <Archive size={16} />, tone: "danger", divider: true, hidden: !editable, onClick: () => setConfirm({ kind: "item", target: item }) },
    ].map((entry) => ({ ...entry, disabled: busy && entry.key !== "history" }));
  };

  const saveImage = safe(async () => {
    if (!imageFile) return;
    await save(() => uploadItemImageAPI(menu._id, imageTarget._id, imageFile));
    setImageTarget(null);
  });

  // --------------------------------------------------------- section actions
  const saveSection = safe(async () => {
    const data = { name: sectionForm.data.name };
    if (sectionForm.mode === "add") await save(() => createSectionAPI(menu._id, data));
    else await save(() => updateSectionAPI(menu._id, sectionForm.section._id, data));
    setSectionForm(null);
  });

  const runConfirm = safe(async () => {
    const { kind, target } = confirm;
    if (kind === "item") await save(() => archiveItemAPI(menu._id, target._id));
    if (kind === "section") await save(() => archiveSectionAPI(menu._id, target._id));
    if (kind === "menu") await save(() => archiveMenuAPI(menu._id), a.menuArchived);
    if (kind === "restoreMenu") await save(() => restoreMenuAPI(menu._id), a.menuRestored);
    if (kind === "removeImage") await save(() => removeItemImageAPI(menu._id, target._id));
    if (kind === "import") await save(() => importIntoMenuAPI(menu._id, { sections: target.sections, mode: target.mode }), t("تم استيراد الملف", "File imported"));
    setConfirm(null);
  });

  // ---------------------------------------------------------------- ordering
  const dragEnabled = editable && !search && filter === "all" && !sendActive;
  const sectionDragProps = (section) =>
    dragEnabled
      ? {
          draggable: true,
          onDragStart: () => setDrag({ type: "section", id: String(section._id) }),
          onDragOver: (event) => event.preventDefault(),
          onDragEnter: () => {
            if (drag?.type !== "section" || drag.id === String(section._id)) return;
            const ids = (sectionOrder || menu.sections.map((s) => String(s._id))).slice();
            setSectionOrder(move(ids, ids.indexOf(drag.id), ids.indexOf(String(section._id))));
          },
          onDragEnd: safe(async () => {
            const ids = sectionOrder;
            setDrag(null);
            if (!ids || ids.join() === menu.sections.map((s) => String(s._id)).join()) return setSectionOrder(null);
            await save(() => reorderSectionsAPI(menu._id, ids)).finally(() => setSectionOrder(null));
            return undefined;
          }),
        }
      : {};

  const itemDragProps = (section, item) =>
    dragEnabled
      ? {
          draggable: true,
          onDragStart: (event) => {
            event.stopPropagation();
            setDrag({ type: "item", sectionId: String(section._id), id: String(item._id) });
          },
          onDragEnter: (event) => {
            event.stopPropagation();
            if (drag?.type !== "item" || drag.sectionId !== String(section._id) || drag.id === String(item._id)) return;
            const live = menu.sections.find((s) => String(s._id) === drag.sectionId).items.map((i) => String(i._id));
            const ids = itemOrder?.sectionId === drag.sectionId ? itemOrder.ids.slice() : live;
            setItemOrder({ sectionId: drag.sectionId, ids: move(ids, ids.indexOf(drag.id), ids.indexOf(String(item._id))) });
          },
          onDragEnd: safe(async (event) => {
            event?.stopPropagation?.();
            const order = itemOrder;
            setDrag(null);
            if (!order) return undefined;
            const live = menu.sections.find((s) => String(s._id) === order.sectionId).items.map((i) => String(i._id));
            if (order.ids.join() === live.join()) return setItemOrder(null);
            await save(() => reorderItemsAPI(menu._id, order.sectionId, order.ids)).finally(() => setItemOrder(null));
            return undefined;
          }),
        }
      : null;

  // -------------------------------------------------------------- menu-level
  const openHistory = async (itemId) => {
    if (pagedHistory) {
      historyItemId.current = itemId;
      return pagedMenuHistory.openHistory();
    }
    setHistory({ open: true, loading: true, list: [] });
    try {
      setHistory({ open: true, loading: false, list: await fetchMenuHistoryAPI(menu._id, itemId) });
    } catch {
      setHistory({ open: true, loading: false, list: [] });
    }
  };

  // mode: "replace" archives the current sections; "append" keeps them.
  const handleImportFile = safe(async (event, mode = "replace") => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const { sections } = transformMenuData(await readMenuFile(file));
    setConfirm({ kind: "import", target: { sections, mode } });
  });

  const handleDuplicate = safe(async () => {
    const result = await save(() => duplicateMenuAPI(menu._id), t("تم نسخ قائمة الطعام كنسخة مستقلة", "Menu duplicated as an independent copy"));
    if (result?.menu?._id) onSelectMenu(result.menu._id);
  });

  const handleCopySubmit = safe(async ({ targets }) => {
    // The per-card copy stays inside the open restaurant: check it right before the call.
    if (!can("menu.manage", { restaurantId: menu.restaurant?.id })) {
      const denied = Object.assign(new Error("Not allowed"), { status: 403 });
      setNotice({ type: "error", text: menuErrorMessage(denied, lang) });
      throw denied;
    }
    let results;
    try {
      results = await copyMenuContentAPI({ type: copyContext.type, source: copyContext.source, targets });
    } catch (error) {
      setNotice({ type: "error", text: menuErrorMessage(error, lang) });
      throw error;
    }
    const dropped = results.reduce((n, r) => n + (Number(r.droppedAdditionLinks) || 0), 0);
    setNotice({ type: "success", text: `${t("تم النسخ", "Copied")}${dropped ? ` · ${t(`لم تُنسخ ${dropped} إضافة غير متاحة`, `${dropped} unavailable addition link(s) not copied`)}` : ""}` });
    // A copy may land in this very menu: reload it and the summaries.
    onMenuChange(null);
    setCopyContext(null);
  });

  const saveName = safe(async () => {
    await save(() => updateMenuPropsAPI(menu._id, { name: nameForm.name }));
    setNameForm(null);
  });

  return (
    <div className="food-shell">
      <FoodTopbar
        menus={menus}
        menu={menu}
        role={role}
        busy={busy}
        onSelectMenu={onSelectMenu}
        panel={panel}
        onOpenPanel={setPanel}
        onHistory={() => openHistory()}
        onRestore={() => setConfirm({ kind: "restoreMenu", target: menu })}
        onExport={() => handleExportMenuExcel(menu)}
        onImportFile={handleImportFile}
        onRename={() => setNameForm({ name: menu.name })}
        onActivate={safe(() => save(() => activateMenuAPI(menu._id), t("تم تفعيل قائمة الطعام", "Menu activated")))}
        onArchive={() => setConfirm({ kind: "menu", target: menu })}
        onDuplicate={handleDuplicate}
      />

      {canManageAdmin && (
        <SelectedMenuSummary menu={menu} assignedBranches={branchOptions} itemFilter={filter} onItemFilter={focusItems} onOpenPanel={setPanel} />
      )}

      {notice && (
        <div className={`menu-notice menu-notice-${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>
          <span>{notice.text}</span>
          <Button type="button" variant="warning" appearance="soft" size="xs" rounded icon={<CircleX size={14} />} aria-label={t("إغلاق", "Dismiss")} onClick={() => setNotice(null)} />
        </div>
      )}

      {menu.lifecycle === "archived" && (
        <NoticeBox variant="neutral" size="sm">
          {a.archivedReadOnly}
        </NoticeBox>
      )}

      {panel === "branches" && <MenuBranchesPanel menu={menu} otherMenus={allMenus.filter((m) => String(m.restaurant?._id) === String(menu.restaurant._id))} onMenuChange={onMenuChange} />}
      {panel === "archived" && editable && <ArchivedContentPanel menu={menu} onMenuChange={applySaved} />}

      {panel === "additions" ? (
        <MenuAdditionsModal menu={menu} restaurants={restaurants} onAdditionsChange={setMenuAdditions} onMutated={() => onMenuChange(null)} />
      ) : (
        <>
          <div className="menu-toolbar" id="menu-items-toolbar">
            <Input type="search" placeholder={t("بحث في الأصناف", "Search items")} aria-label={t("بحث في الأصناف", "Search items")} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} />
            <Select
              value={filter}
              fullWidth={false}
              onChange={(e) => setFilter(e.target.value)}
              options={ITEM_FILTERS.map((value) => ({ value, label: summaryLabels.filters[value] }))}
            />
            {branchOptions.length > 0 && (
              <Select
                value={selectedBranchId || "all"}
                fullWidth={false}
                onChange={(e) => setSelectedBranchId(e.target.value === "all" ? "" : e.target.value)}
                options={[...(role.isBranchUser ? [] : [{ value: "all", label: t("كل الفروع", "All branches") }]), ...branchOptions.map((b) => ({ value: String(b._id), label: b.name }))]}
              />
            )}
          </div>

          {sendActive && (
            <SendToAppBar
              menuId={menu._id}
              storeId={menu.restaurant?.id}
              selectedIds={[...selectedItemIds]}
              action={sendAction}
              onActionChange={setSendAction}
              totalCount={shownItems.length}
              availableCount={availableShown.length}
              checking={!eligibility || eligibilityAction !== (sendAction || "update")}
              canSend={canSend}
              onSelectAll={selectAllShown}
              onClear={() => setSelectedIds(new Set())}
              onCancel={() => onSendModeChange?.(false)}
              onError={(text, error) => {
                setNotice({ type: "error", text });
                // Stale view (an item became hidden/incomplete): re-read what can be sent.
                if (error?.code === "ITEMS_NOT_ELIGIBLE") loadEligibility();
              }}
              onSent={({ sends, message, type }) => {
                if (sends) setItemSends(sends);
                setNotice({ type: type || "success", text: message });
                setSelectedIds(new Set());
                onSendModeChange?.(false);
              }}
            />
          )}

          <div className="food-body">
            <FoodSidebar
              sections={orderedSections}
              editable={editable}
              onAddSection={() => setSectionForm({ mode: "add", data: { name: { ar: "", en: "" } } })}
              onCopyAll={editable ? () => setCopyContext({ type: "SECTIONS", source: { menuId: menu._id } }) : undefined}
              dragSectionId={drag?.type === "section" ? drag.id : null}
              sectionDragProps={dragEnabled ? sectionDragProps : null}
            />

            <main className="food-content">
              {visibleSections.length === 0 ? (
                <div className="food-empty">
                  <p>{filter !== "all" && orderedSections.length ? summaryLabels.noItemsMatch : t("لا توجد أقسام بعد", "No sections yet")}</p>
                  {filter !== "all" && orderedSections.length > 0 && (
                    <Button type="button" variant="primary" appearance="soft" size="sm" rounded onClick={() => setFilter("all")}>
                      {summaryLabels.clearFilter}
                    </Button>
                  )}
                </div>
              ) : (
                visibleSections.map((section) => (
                  <FoodSection
                    key={section._id}
                    section={section}
                    menu={menu}
                    role={role}
                    selectedBranchId={selectedBranchId}
                    expandedCard={expandedCard}
                    setExpandedCard={setExpandedCard}
                    selectedOptions={selectedOptions}
                    setSelectedOptions={setSelectedOptions}
                    isDragging={drag?.type === "section" && drag.id === String(section._id)}
                    sectionDragProps={sectionDragProps(section)}
                    itemDragProps={itemDragProps}
                    dragItemId={drag?.type === "item" ? drag.id : null}
                    onAddItem={(target) => setItemForm({ mode: "add", section: target, data: emptyItem() })}
                    sectionActions={sectionActions(section)}
                    itemActions={itemActions}
                    itemHandlers={itemHandlers}
                    labels={{ addItem: a.addItem, sectionActions: a.sectionActions, itemActions: a.itemActions }}
                    selectMode={sendActive}
                    selectedItemIds={selectedItemIds}
                    onToggleSelect={toggleSelected}
                    itemSends={itemSends}
                    onOpenSendHistory={setSendHistoryItem}
                    sendBlockFor={sendBlockFor}
                  />
                ))
              )}
            </main>
          </div>
        </>
      )}

      {itemForm && (
        <FoodItemForm
          mode={itemForm.mode}
          itemData={itemForm.data}
          setItemData={(data) => setItemForm((prev) => ({ ...prev, data }))}
          onSave={saveItem}
          onCancel={() => setItemForm(null)}
          menuAdditions={menuAdditions}
          busy={busy}
          error={notice?.type === "error" ? notice.text : ""}
        />
      )}

      {sectionForm && (
        <AddSectionModal
          mode={sectionForm.mode}
          newSectionData={sectionForm.data}
          setNewSectionData={(data) => setSectionForm((prev) => ({ ...prev, data }))}
          handleAddSection={saveSection}
          onCancel={() => setSectionForm(null)}
          busy={busy}
          error={notice?.type === "error" ? notice.text : ""}
        />
      )}

      {imageTarget && <FoodImageModal imageFile={imageFile} setImageFile={setImageFile} imagePreview={imagePreview} setImagePreview={setImagePreview} onSave={saveImage} onCancel={() => setImageTarget(null)} />}

      {nameForm && (
        <div className="food-modal-overlay">
          <div className="food-modal" role="dialog" aria-modal="true" aria-label={t("اسم قائمة الطعام", "Menu name")}>
            <h3>{t("اسم قائمة الطعام", "Menu name")}</h3>
            <Input label={t("اسم قائمة الطعام", "Menu name")} value={nameForm.name} onChange={(e) => setNameForm({ name: e.target.value })} />
            <div className="food-modal-actions">
              <Button type="button" variant="primary" appearance="filled" disabled={busy} onClick={saveName}>
                {t("حفظ", "Save")}
              </Button>
              <Button type="button" variant="warning" appearance="soft" onClick={() => setNameForm(null)}>
                {t("إلغاء", "Cancel")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          tone={["menu", "item", "section", "removeImage"].includes(confirm.kind) ? "danger" : undefined}
          title={
            {
              import: confirm.target?.mode === "append" ? a.confirmAppend : t("استبدال محتوى قائمة الطعام بالملف؟", "Replace the menu content with the file?"),
              menu: a.confirmArchiveMenu,
              restoreMenu: a.confirmRestoreMenu,
              section: a.confirmArchiveSection,
              item: a.confirmArchiveItem,
              removeImage: a.confirmRemoveImage,
            }[confirm.kind]
          }
          message={[
            confirm.kind === "import" ? (confirm.target.mode === "append" ? a.confirmAppendBody : t("الأقسام الحالية ستُؤرشف ولن تُحذف.", "Current sections are archived, not deleted.")) : localized(confirm.target?.name, lang) || confirm.target?.name,
            { menu: a.confirmArchiveMenuBody, restoreMenu: a.confirmRestoreMenuBody, section: a.confirmArchiveSectionBody, item: a.confirmArchiveItemBody }[confirm.kind],
          ]
            .filter(Boolean)
            .join("\n")}
          confirmText={{ restoreMenu: a.restore, removeImage: a.removeImage, menu: a.archive, section: a.archive, item: a.archive }[confirm.kind]}
          cancelText={a.cancel}
          onConfirm={runConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {sendHistoryItem && <SendHistoryDialog menuId={menu._id} item={sendHistoryItem} onClose={() => setSendHistoryItem(null)} />}

      {dialog?.kind === "visibility" && <ItemVisibilityDialog menu={menu} item={dialog.item} branches={branchOptions} onClose={() => setDialog(null)} onSaved={applySaved} />}
      {dialog?.kind === "move" && <MoveItemDialog menu={menu} item={dialog.item} sections={menu.sections} currentSectionId={dialog.sectionId} onClose={() => setDialog(null)} onSaved={applySaved} />}

      {pagedHistory ? (
        <MenuHistoryModal
          open={pagedMenuHistory.open}
          history={pagedMenuHistory.list}
          loading={pagedMenuHistory.loading}
          loadingMore={pagedMenuHistory.loadingMore}
          hasMore={pagedMenuHistory.hasMore}
          error={pagedMenuHistory.error}
          moreError={pagedMenuHistory.moreError}
          onLoadMore={pagedMenuHistory.loadMore}
          onRetry={pagedMenuHistory.retry}
          onClose={pagedMenuHistory.close}
        />
      ) : (
        <MenuHistoryModal open={history.open} history={history.list} loading={history.loading} onClose={() => setHistory({ open: false, loading: false, list: [] })} />
      )}

      {copyContext && <MenuSelectionModal menus={allMenus.filter((m) => m.lifecycle !== "archived" && String(m.restaurant?._id) === String(menu.restaurant._id))} copyType={copyContext.type} onSubmit={handleCopySubmit} onClose={() => setCopyContext(null)} />}
    </div>
  );
}
